// components/modals/change-logs/changes-diff-modal.js
import { Modal, Spin, Tag } from "antd";
import React from "react";
import { FormImage } from "../../form/form";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { marked } from "marked";
import SimpleColorPicker from "../../../components/color-picker/color-picker";

/** types: string, number, boolean, url, entity, array, array-entity, enum, image, text, color */

const LABEL_WIDTH = 140;
const COL_GAP = 16;
const ROW_PADDING_LEFT = 0;
const ROW_PADDING_VERTICAL = 8;
const ROW_GAP = 16;

const TITLE_VALUE_STYLE = {
  fontWeight: 700,
  fontSize: 24,
  lineHeight: "24px",
  wordBreak: "break-word",
};

const CHANGED_BAR = "#FF3B30";
const CHANGED_BG = "rgba(255, 59, 48, 0.10)";

/* ===================== LCS diff ===================== */

const refineWordChange = (prevW = "", currW = "") => {
  let i = 0;
  while (i < prevW.length && i < currW.length && prevW[i] === currW[i]) i++;

  let k = 0;
  while (
    k < (prevW.length - i) &&
    k < (currW.length - i) &&
    prevW[prevW.length - 1 - k] === currW[currW.length - 1 - k]
  ) {
    k++;
  }

  const prefix = prevW.slice(0, i);
  const prevMid = prevW.slice(i, prevW.length - k);
  const currMid = currW.slice(i, currW.length - k);
  const suffix = prevW.slice(prevW.length - k);

  return { prefix, prevMid, currMid, suffix };
};

const splitWords = (s = "") => {
  const words = [];
  const spaces = [];
  const re = /(\S+)(\s*)/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    words.push(m[1]);
    spaces.push(m[2] ?? "");
  }
  return { words, spaces };
};

const lcsOps = (prevWords, currWords) => {
  const n = prevWords.length;
  const m = currWords.length;
  const dp = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = prevWords[i] === currWords[j]
        ? 1 + dp[i + 1][j + 1]
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (prevWords[i] === currWords[j]) {
      ops.push({ type: "equal", words: [currWords[j]] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ type: "del", words: [prevWords[i++]] });
    } else {
      ops.push({ type: "add", words: [currWords[j++]] });
    }
  }
  while (i < n) ops.push({ type: "del", words: [prevWords[i++]] });
  while (j < m) ops.push({ type: "add", words: [currWords[j++]] });

  // merge same-type
  const merged = [];
  for (const op of ops) {
    const last = merged[merged.length - 1];
    if (last && last.type === op.type) last.words.push(...op.words);
    else merged.push({ ...op });
  }
  return merged;
};
/* ================================================================ */

const escapeHtml = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const highlightTextDiff = (prevText = "", currText = "") => {
  const { words: pw } = splitWords(prevText);
  const { words: cw, spaces: cs } = splitWords(currText);
  const ops = lcsOps(pw, cw);

  let html = "";
  let currIdx = 0;

  for (let idx = 0; idx < ops.length; idx++) {
    const op = ops[idx];

    if (
      op.type === "del" &&
      op.words.length === 1 &&
      idx + 1 < ops.length &&
      ops[idx + 1].type === "add" &&
      ops[idx + 1].words.length === 1
    ) {
      const prevW = op.words[0];
      const currW = ops[idx + 1].words[0];

      const { prefix, prevMid, currMid, suffix } = refineWordChange(prevW, currW);

      if (prefix) html += escapeHtml(prefix);
      if (currMid) html += `<ins style="color:${CHANGED_BAR};text-decoration:none;">${escapeHtml(currMid)}</ins>`;
      if (suffix) html += escapeHtml(suffix);

      if (prevMid) html += `<del style="color:${CHANGED_BAR};">${escapeHtml(prevMid)}</del>`;

      html += cs[currIdx] ?? "";
      currIdx += 1;

      idx += 1;
      continue;
    }

    if (op.type === "equal") {
      for (let k = 0; k < op.words.length; k++) {
        const w = op.words[k];
        html += `${escapeHtml(w)}${cs[currIdx++] ?? ""}`;
      }
      continue;
    }

    if (op.type === "add") {
      let chunk = "";
      for (let k = 0; k < op.words.length; k++) {
        const w = op.words[k];
        chunk += `${escapeHtml(w)}${cs[currIdx++] ?? ""}`;
      }
      html += `<ins style="color:${CHANGED_BAR};text-decoration:none;">${chunk}</ins>`;
      continue;
    }

    if (op.type === "del") {
      const chunk = escapeHtml(op.words.join(" ")) + " ";
      html += `<del style="color:${CHANGED_BAR};">${chunk}</del>`;
      continue;
    }
  }

  return html;
};

const buildHtmlDiffFromMarkdown = (prevMd = "", currMd = "") => {
  const prevHTML = marked.parse(prevMd || "");
  const currHTML = marked.parse(currMd || "");

  const parser = new DOMParser();
  const prevDoc = parser.parseFromString(prevHTML, "text/html");
  const currDoc = parser.parseFromString(currHTML, "text/html");

  const prevTexts = collectTextNodes(prevDoc.body);
  const currTexts = collectTextNodes(currDoc.body);

  let p = 0;

  for (let c = 0; c < currTexts.length; c++) {
    const currNode = currTexts[c];
    const currText = currNode.nodeValue || "";
    const prevText = p < prevTexts.length ? (prevTexts[p].nodeValue || "") : "";

    if (currText !== prevText) {
      const html = highlightTextDiff(prevText, currText);

      const span = currDoc.createElement("span");
      span.innerHTML = html;
      if (span.innerHTML !== escapeHtml(currText)) {
        currNode.replaceWith(...span.childNodes);
      }
    }
    p++;
  }

  return currDoc.body.innerHTML;
};

// const collectTextNodes = (root) => {
//   const out = [];
//   const walker = (node) => {
//     if (node.nodeType === Node.TEXT_NODE) {
//       if (node.nodeValue && node.nodeValue.trim() !== "") out.push(node);
//     } else {
//       for (const child of node.childNodes) walker(child);
//     }
//   };
//   walker(root);
//   return out;
// };

const collectTextNodes = (root) => {
  const out = [];
  const walker = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.nodeValue && node.nodeValue.trim() !== "") out.push(node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = (node.tagName || "").toLowerCase();

      if (["img", "video", "audio", "source", "iframe"].includes(tag)) {
        return;
      }

      for (const child of node.childNodes) walker(child);
    }
  };
  walker(root);
  return out;
};

/* ================================== */
const formatBool = (v) => (v ? "Yes" : "No");
const normalizeArray = (arr) => (Array.isArray(arr) ? arr : []);
const toKey = (x) => (x == null ? "" : String(x));

// case-insensitive compare for colors like "#ABC" vs "#abc"
const normalizeColor = (v) => (typeof v === "string" ? v.trim().toLowerCase() : v);

const isValueChanged = (type, prevVal, currVal, field = {}) => {
  switch (type) {
    case "string":
    case "number":
    case "url":
    case "enum":
      return (prevVal ?? "") !== (currVal ?? "");
    case "boolean":
      return Boolean(prevVal) !== Boolean(currVal);
    case "entity": {
      const getId = field.getId || ((x) => x);
      return getId(prevVal) !== getId(currVal);
    }
    case "array": {
      const a = normalizeArray(prevVal).map(String);
      const b = normalizeArray(currVal).map(String);
      if (a.length !== b.length) return true;
      const as = new Set(a);
      for (const v of b) if (!as.has(v)) return true;
      return false;
    }
    case "array-entity": {
      const getId = field.getId || ((x) => (typeof x === "object" ? x?.id : x));
      const a = normalizeArray(prevVal).map(getId).map(String);
      const b = normalizeArray(currVal).map(getId).map(String);
      if (a.length !== b.length) return true;
      const as = new Set(a);
      for (const v of b) if (!as.has(v)) return true;
      return false;
    }
    case "color": {
      return normalizeColor(prevVal) !== normalizeColor(currVal);
    }
    case "image":
    case "text":
    default:
      return false;
  }
};
/* ===================================================================== */

const ColorSwatch = ({ value }) => {
  const hasValue = typeof value === "string" && value.trim() !== "";
  if (!hasValue) return <i>—</i>;
  return (
    <SimpleColorPicker
      readonly={true}
      defaultValue={value}
      noPadding={true}
    />
  );
};

const SimpleValue = ({ type, value, field }) => {
  if (type === "boolean") {
    if (value === null || value === undefined) {
      return <i>—</i>;
    }
    return <span>{formatBool(value)}</span>;
  }
  if (type === "url") {
    return value ? <a href={value} target="_blank" rel="noreferrer">{value}</a> : <i>—</i>;
  }
  if (type === "entity") {
    const label = field?.resolve ? field.resolve(value) : value;
    return <span>{label == null || label === "" ? <i>—</i> : String(label)}</span>;
  }
  if (type === "array") {
    const vals = normalizeArray(value);
    return vals.length ? vals.map((v) => <Tag key={toKey(v)}>{String(v)}</Tag>) : <i>—</i>;
  }
  if (type === "array-entity") {
    const names = field?.resolveMany ? field.resolveMany(value) : normalizeArray(value);
    return names?.length ? names.map((n, i) => <Tag key={i}>{String(n)}</Tag>) : <i>—</i>;
  }
  if (type === "enum") return <span>{field?.displayMap?.[value] ?? String(value ?? "—")}</span>;
  if (type === "color") return <ColorSwatch value={value} />;
  return <span>{value == null || value === "" ? <i>—</i> : String(value)}</span>;
};

const RowTitle = ({ field, prev, curr }) => {
  const aVal = prev?.[field.key];
  const bVal = curr?.[field.key];
  const changed = isValueChanged("string", aVal, bVal);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: COL_GAP }}>
      <div style={{ paddingTop: ROW_PADDING_VERTICAL, paddingBottom: ROW_PADDING_VERTICAL }}>
        <div style={TITLE_VALUE_STYLE}>
          {aVal == null || aVal === "" ? <i>—</i> : String(aVal)}
        </div>
      </div>
      <div
        style={{
          position: "relative",
          background: changed ? CHANGED_BG : undefined,
          borderRadius: changed ? 4 : undefined,
          paddingLeft: ROW_PADDING_LEFT,
          paddingTop: ROW_PADDING_VERTICAL,
          paddingBottom: ROW_PADDING_VERTICAL,
        }}
      >
        {changed && (
          <div
            style={{
              position: "absolute",
              left: 0, top: 0, bottom: 0,
              width: 4, background: CHANGED_BAR, borderRadius: 8,
            }}
          />
        )}
        <div style={TITLE_VALUE_STYLE}>
          {bVal == null || bVal === "" ? <i>—</i> : String(bVal)}
        </div>
      </div>
    </div>
  );
};

const RowStandard = ({ field, prev, curr }) => {
  const type = field.type || "string";
  const aVal = prev?.[field.key];
  const bVal = curr?.[field.key];
  const changed = isValueChanged(type, aVal, bVal, field);

  const LabelValue = ({ label, children }) => (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div
        style={{
          flex: `0 0 ${LABEL_WIDTH}px`,
          maxWidth: LABEL_WIDTH,
          color: "rgba(40, 40, 40, 0.7)",
          fontSize: 14,
          lineHeight: "20px",
          paddingTop: 2,
        }}
      >
        {label}
      </div>
      <div style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>{children}</div>
    </div>
  );

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: COL_GAP }}>
      <div style={{ paddingTop: ROW_PADDING_VERTICAL, paddingBottom: ROW_PADDING_VERTICAL }}>
        <LabelValue label={field.label}>
          <SimpleValue type={type} value={aVal} field={field} />
        </LabelValue>
      </div>

      <div
        style={{
          position: "relative",
          background: changed ? CHANGED_BG : undefined,
          borderRadius: changed ? 4 : undefined,
          paddingLeft: ROW_PADDING_LEFT,
          paddingTop: ROW_PADDING_VERTICAL,
          paddingBottom: ROW_PADDING_VERTICAL,
        }}
      >
        {changed && (
          <div
            style={{
              position: "absolute",
              left: 0, top: 0, bottom: 0,
              width: 4, background: CHANGED_BAR, borderRadius: 8,
            }}
          />
        )}
        <LabelValue label={field.label}>
          <SimpleValue type={type} value={bVal} field={field} />
        </LabelValue>
      </div>
    </div>
  );
};

const RowBlock = ({ field, prev, curr }) => {
  const type = field.type; // 'image' | 'text'
  const aVal = prev?.[field.key] ?? "";
  const bVal = curr?.[field.key] ?? "";

  const BlockLabel = ({ label }) =>
    label ? <div style={{ color: "#555", fontWeight: 600, fontSize: 12, marginBottom: 6 }}>{label}</div> : null;

  const renderImage = (val, idForFallback) => (
    <FormImage
      readonly
      value={typeof val === "string" ? val : val?.url || ""}
      articleId={idForFallback}
      onChange={() => {}}
      updateImageStatus={() => {}}
      updateIsUpdatedImage={() => {}}
    />
  );

  const renderTextPrev = (val) => (
    <div className="diff-text">
      <Markdown rehypePlugins={[rehypeRaw]}>{val}</Markdown>
    </div>
  );

  const renderTextCurr = (prevMd, currMd) => {
    const diffHtml = buildHtmlDiffFromMarkdown(prevMd, currMd);
    return (
      <div
        className="diff-text"
        dangerouslySetInnerHTML={{ __html: diffHtml }}
      />
    );
  };

  const LeftCol = (
    <div style={{ paddingTop: ROW_PADDING_VERTICAL, paddingBottom: ROW_PADDING_VERTICAL }}>
      <BlockLabel label={field.label} />
      {type === "image" ? renderImage(aVal, prev?.id) : renderTextPrev(aVal)}
    </div>
  );

  const RightCol = (
    <div style={{ paddingLeft: ROW_PADDING_LEFT, paddingTop: ROW_PADDING_VERTICAL, paddingBottom: ROW_PADDING_VERTICAL }}>
      <BlockLabel label={field.label} />
      {type === "image" ? renderImage(bVal, curr?.id) : renderTextCurr(aVal, bVal)}
    </div>
  );

  return (
    <>
      <style>{`
        .changes-diff-modal .diff-text {
          white-space: normal; /* було pre-wrap -> давав зайві великі інтервали */
          word-break: break-word;
        }
        .changes-diff-modal .diff-text ins {
          color: ${CHANGED_BAR};
          text-decoration: none;
          background: none;
        }
        .changes-diff-modal .diff-text del {
          color: ${CHANGED_BAR};
          text-decoration: line-through;
          background: none;
        }
        .changes-diff-modal .diff-text p { margin: 0 0 8px; }
        .changes-diff-modal .diff-text ul,
        .changes-diff-modal .diff-text ol {
          margin: 0 0 8px 1.25rem;
          padding-left: 1.25rem;
          list-style-position: outside;
        }
        .changes-diff-modal .diff-text ul { list-style-type: disc !important; }
        .changes-diff-modal .diff-text ol { list-style-type: decimal !important; }
      `}</style>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: COL_GAP }}>
        {LeftCol}
        {RightCol}
      </div>
    </>
  );
};

const ChangesDiffModalComponent = ({
  title,
  handleCancel,
  isModalOpen,
  loading,
  pending,         // Current
  active,          // Previous
  fields = [],
  titleFieldKey,
}) => {
  const prev = active;
  const curr = pending;

  const renderRow = (f) => {
    if (f.key === titleFieldKey) return <RowTitle key={f.key} field={f} prev={prev} curr={curr} />;
    if (f.type === "image" || f.type === "text") return <RowBlock key={f.key} field={f} prev={prev} curr={curr} />;
    return <RowStandard key={f.key} field={f} prev={prev} curr={curr} />;
  };

  return (
    <Modal
      wrapClassName="lf-changes-diff-modal--top"
      title={
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: COL_GAP, alignItems: "center" }}>
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <span className="drawer-header__badge">Previous</span>
          </div>
          <div style={{ marginLeft: 18, display: "flex", justifyContent: "flex-start" }}>
            <span className="drawer-header__badge">Current</span>
          </div>
        </div>
      }
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      width="80%"
      className="changes-diff-modal"
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <Spin />
        </div>
      ) : (
        <div style={{ display: "grid", rowGap: ROW_GAP }}>
          {fields.map(renderRow)}
        </div>
      )}
    </Modal>
  );
};

export default ChangesDiffModalComponent;
